/**
 * v0.11 (plan P2, rev-2 F-F) — the shared, space-aware `do`-splice scope
 * builder. One implementation of the scope walk that graph edge derivation
 * (`subprocessScopeFor` / `addProcessSpliceFallbacks`) and the walker
 * context (`walkerContextFor`) both consume, so the two can never drift.
 *
 * Scope semantics (the pre-v0.11 contract, now space-chain aware):
 *   - free-standing entries are global; with same-name candidates in
 *     several spaces, the invoker's space chain resolves nearest-first,
 *     with the D3a global-unique fallback (cross-space coupling stays
 *     statable — flagged, never silently dropped);
 *   - module-scoped entries apply when their owner path is a prefix of the
 *     invoking process's owner AND their space is on the invoker's chain
 *     (a bare owner path is only meaningful within its space); closer
 *     owners win, then nearer spaces;
 *   - every module-scoped entry is also addressable by its qualified
 *     `<owner>.<name>` (per-family visibility: subprocess FQNs stay
 *     scope-limited, process FQNs are global) and — new in v0.11 (D3) — by
 *     its space-extended `<space>.<owner>.<name>`, the written form that
 *     disambiguates cross-space targets;
 *   - process-nested entries (the walker's `ownerProcess` layer) are the
 *     caller's concern — they win last and are not handled here.
 *
 * Every legal pre-v0.11 model has single-candidate keys (cross-space
 * same-names were DUPLICATE_* errors), so resolution is byte-identical
 * there by construction.
 */

import type { QualifiedName } from "./types.js";
import { resolveScoped, sameSpace, type ScopedCand } from "./hoist-index.js";
import { spaceChain } from "./resolver.js";

export interface SpliceEntry {
  readonly name: string;
  readonly ownerPath?: QualifiedName;
  readonly space?: QualifiedName;
}

export interface SpliceInvoker {
  readonly ownerPath?: QualifiedName;
  readonly space?: QualifiedName;
}

function isPrefix(a: readonly string[], b: readonly string[]): boolean {
  return a.length <= b.length && a.every((seg, i) => seg === b[i]);
}

/** Index of `space` on the invoker's chain (0 = own space), or -1 off-chain. */
function chainRank(
  chain: readonly (QualifiedName | undefined)[],
  space: QualifiedName | undefined,
): number {
  for (let i = 0; i < chain.length; i++) if (sameSpace(chain[i], space)) return i;
  return -1;
}

/**
 * Build the splice scope for `invoker` over `all` entries of one family.
 * Returns the key → entry map (bare names, qualified FQNs, space-extended
 * FQNs). `globalFqn` — process semantics (FQN addressable from anywhere)
 * vs subprocess semantics (FQN scope-limited to the chain).
 */
export function buildSpliceScope<T extends SpliceEntry>(
  all: readonly T[],
  invoker: SpliceInvoker,
  opts: { globalFqn: boolean },
): Map<string, T> {
  const m = new Map<string, T>();
  const owner = invoker.ownerPath ?? [];
  const chain = spaceChain(invoker.space);

  // Free-standing: per-name candidates resolved over the chain (D3a
  // fallback inside resolveScoped). The ScopedCand path is space-extended so
  // the global-distinct check tells two spaces' same-named entries apart.
  // Within ONE space, the historical last-write-wins is preserved by
  // collapsing per space to the LAST entry (an already-red duplicate model
  // keeps binding exactly as before).
  const free = new Map<string, Map<string, ScopedCand<T>>>();
  const scoped: T[] = [];
  const cand = (e: T): ScopedCand<T> => ({
    value: e,
    path: [...(e.space ?? []), ...(e.ownerPath ?? []), e.name],
    space: e.space,
  });
  for (const e of all) {
    if (!e.ownerPath || e.ownerPath.length === 0) {
      let perSpace = free.get(e.name);
      if (!perSpace) free.set(e.name, (perSpace = new Map()));
      perSpace.set((e.space ?? []).join("."), cand(e)); // last per space wins
    } else if (isPrefix(e.ownerPath, owner) && chainRank(chain, e.space) >= 0) {
      scoped.push(e);
    }
  }
  for (const [name, perSpace] of free) {
    const r = resolveScoped([...perSpace.values()], invoker.space);
    if (r && r !== "ambiguous") m.set(name, r.value);
  }

  // Module-scoped, on-chain: closer owners win, then nearer spaces (sort
  // farther-first so later writes are nearer). Pre-v0.11 both extra sort
  // terms are constant (one space), preserving the historical order.
  scoped.sort(
    (a, b) =>
      (a.ownerPath?.length ?? 0) - (b.ownerPath?.length ?? 0) ||
      chainRank(chain, b.space) - chainRank(chain, a.space),
  );
  for (const e of scoped) {
    m.set(e.name, e);
    m.set([...(e.ownerPath ?? []), e.name].join("."), e);
  }

  // Qualified addressing beyond the chain. Process FQNs are globally
  // addressable (`do Notifications.NotifyCustomer` from anywhere) — with
  // same-FQN candidates in several spaces, the chain resolves; off-chain
  // global-unique falls back (D3a). Subprocess FQNs stay scope-limited
  // (already registered above).
  if (opts.globalFqn) {
    // Per space keep the FIRST entry (the historical `!has(fqn)` guard),
    // then resolve cross-space candidate sets over the chain.
    const byFqn = new Map<string, Map<string, ScopedCand<T>>>();
    for (const e of all) {
      if (!e.ownerPath || e.ownerPath.length === 0) continue;
      const fqn = [...e.ownerPath, e.name].join(".");
      let perSpace = byFqn.get(fqn);
      if (!perSpace) byFqn.set(fqn, (perSpace = new Map()));
      const sk = (e.space ?? []).join(".");
      if (!perSpace.has(sk)) perSpace.set(sk, cand(e)); // first per space wins
    }
    for (const [fqn, perSpace] of byFqn) {
      if (m.has(fqn)) continue; // on-chain registration already won
      const r = resolveScoped([...perSpace.values()], invoker.space);
      if (r && r !== "ambiguous") m.set(fqn, r.value);
    }
  }

  // Space-extended written form (D3): `<space>.<owner>.<name>` names a
  // target exactly, across any boundary (visibility is the soft-flag's
  // concern, not resolution's). A declared entry already holding the key
  // wins — the spec's exact-structural-path-first rule (a module named like
  // a space cannot be shadowed by the space-extended spelling).
  for (const e of all) {
    if (!e.space || e.space.length === 0) continue;
    const key = [...e.space, ...(e.ownerPath ?? []), e.name].join(".");
    if (!m.has(key)) m.set(key, e);
  }

  return m;
}
